import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { cn } from "@/lib/utils";
import { searchPatients } from "@/hooks/useCommandPalettePatients";
import { useAuth } from "@/hooks/useAuth";
import { AnimatePresence, motion } from "framer-motion";
import { FRAMER_TRANSITIONS } from "@/lib/motion";
import { buildPatientProfilePath } from "@/lib/patient-navigation";
import {
    Search,
    Calendar,
    Ticket,
    UserPlus,
    LayoutDashboard,
    Inbox,
    Phone,
    Users,
    Settings,
    Receipt,
    CornerDownLeft,
    User,
    Activity,
} from "lucide-react";

// ── Types ────────────────────────────────────────────────────

interface CommandItem {
    id: string;
    label: string;
    description?: string;
    icon: typeof Search;
    group: string;
    action: () => void;
    keywords?: string[];
}

interface CommandPaletteProps {
    open: boolean;
    onClose: () => void;
}

// ── Component ────────────────────────────────────────────────

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [query, setQuery] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const [patientResults, setPatientResults] = useState<CommandItem[]>([]);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Build command items
    const commands: CommandItem[] = [
        // Actions
        {
            id: "new-appointment",
            label: "Neuer Termin",
            description: "Termin im Kalender erstellen",
            icon: Calendar,
            group: "Aktionen",
            action: () => navigate("/app/calendar?action=create"),
            keywords: ["termin", "buchen", "anlegen"],
        },
        {
            id: "new-ticket",
            label: "Neues Ticket",
            description: "Aufgabe erstellen",
            icon: Ticket,
            group: "Aktionen",
            action: () => navigate("/app/tickets?action=create"),
            keywords: ["ticket", "aufgabe", "task"],
        },
        {
            id: "new-patient",
            label: "Neuer Patient",
            description: "Patient anlegen",
            icon: UserPlus,
            group: "Aktionen",
            action: () => navigate("/app/patients?action=create"),
            keywords: ["patient", "neu", "anlegen"],
        },

        // Pages
        {
            id: "nav-dashboard",
            label: "Dashboard",
            description: "⌘1",
            icon: LayoutDashboard,
            group: "Seiten",
            action: () => navigate("/app/dashboard"),
            keywords: ["dashboard", "übersicht", "start"],
        },
        {
            id: "nav-calendar",
            label: "Kalender",
            description: "⌘2",
            icon: Calendar,
            group: "Seiten",
            action: () => navigate("/app/calendar"),
            keywords: ["kalender", "termine"],
        },
        {
            id: "nav-inbox",
            label: "Postfach",
            description: "⌘3",
            icon: Inbox,
            group: "Seiten",
            action: () => navigate("/app/inbox"),
            keywords: ["postfach", "nachrichten", "messaging"],
        },
        {
            id: "nav-tickets",
            label: "Tickets",
            description: "⌘4",
            icon: Ticket,
            group: "Seiten",
            action: () => navigate("/app/tickets"),
            keywords: ["tickets", "aufgaben"],
        },
        {
            id: "nav-ai-phone",
            label: "KI-Telefon",
            description: "⌘5",
            icon: Phone,
            group: "Seiten",
            action: () => navigate("/app/ai-phone"),
            keywords: ["ki", "telefon", "anrufe", "phone"],
        },
        {
            id: "nav-triage",
            label: "KI-Triage",
            description: "⌘8",
            icon: Activity,
            group: "Seiten",
            action: () => navigate("/app/triage"),
            keywords: ["ki", "triage", "klassifizierung", "inbox", "priorität"],
        },
        {
            id: "nav-patients",
            label: "Patienten",
            description: "⌘6",
            icon: Users,
            group: "Seiten",
            action: () => navigate("/app/patients"),
            keywords: ["patienten", "patientenliste"],
        },
        {
            id: "nav-billing",
            label: "Abrechnung",
            description: "⌘7",
            icon: Receipt,
            group: "Seiten",
            action: () => navigate("/app/billing"),
            keywords: ["abrechnung", "rechnungen", "billing", "goä", "igel"],
        },
        {
            id: "nav-settings",
            label: "Einstellungen",
            description: "⌘9",
            icon: Settings,
            group: "Seiten",
            action: () => navigate("/app/settings"),
            keywords: ["einstellungen", "settings", "team"],
        },

        // Workflows
        {
            id: "wf-rezept",
            label: "Workflow: Rezepte",
            description: "Follow-ups > Rezeptanfragen",
            icon: Receipt,
            group: "Workflows",
            action: () => navigate("/app/follow-ups?category=prescription"),
            keywords: ["workflow", "rezept", "follow-up", "prescription"],
        },
        {
            id: "wf-triage-urgent",
            label: "Workflow: Dringende Triage",
            description: "KI-Triage > dringende Nachrichten",
            icon: Activity,
            group: "Workflows",
            action: () => navigate("/app/triage?tab=urgent"),
            keywords: ["workflow", "triage", "dringend", "urgent"],
        },
        {
            id: "wf-checkin",
            label: "Workflow: Rezeption Check-in",
            description: "Rezeption > Patientenaufnahme",
            icon: UserPlus,
            group: "Workflows",
            action: () => navigate("/app/reception"),
            keywords: ["workflow", "rezeption", "checkin", "aufnahme"],
        },
    ];

    // Filter
    const filtered = query.trim()
        ? commands.filter((cmd) => {
              const q = query.toLowerCase();
              return (
                  cmd.label.toLowerCase().includes(q) ||
                  cmd.description?.toLowerCase().includes(q) ||
                  cmd.keywords?.some((kw) => kw.includes(q))
              );
          })
        : commands;

    // Merge patient results into filtered list
    const allFiltered = [...patientResults, ...filtered];

    // Group
    const groups = Array.from(new Set(allFiltered.map((c) => c.group)));

    // Patient search effect
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (!query.trim() || query.length < 2 || !user?.practiceId) {
            setPatientResults([]);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            try {
                const patients = await searchPatients(user.practiceId!, query);
                const items: CommandItem[] = patients.slice(0, 5).map((p) => ({
                    id: `patient-${p.id}`,
                    label: `${p.firstName} ${p.lastName}`,
                    description: p.dateOfBirth
                        ? `*${new Date(p.dateOfBirth).getFullYear()}`
                        : undefined,
                    icon: User,
                    group: "Patienten",
                    action: () => navigate(buildPatientProfilePath(p.id)),
                    keywords: [],
                }));
                setPatientResults(items);
            } catch {
                setPatientResults([]);
            }
        }, 300);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query, user?.practiceId, navigate]);

    // Reset state on open
    useEffect(() => {
        if (open) {
            setQuery("");
            setSelectedIndex(0);
            setPatientResults([]);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    // Keyboard navigation
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedIndex((i) => Math.min(i + 1, allFiltered.length - 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
                e.preventDefault();
                const item = allFiltered[selectedIndex];
                if (item) {
                    item.action();
                    onClose();
                }
            } else if (e.key === "Escape") {
                onClose();
            }
        },
        [allFiltered, selectedIndex, onClose],
    );

    // Reset selection on query change
    useEffect(() => {
        setSelectedIndex(0);
    }, [query]);

    let flatIndex = -1;

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="fixed inset-0 z-[60] bg-neutral-900/40 backdrop-blur-sm"
                        onClick={onClose}
                        aria-hidden="true"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={FRAMER_TRANSITIONS.component}
                    />

                    {/* Palette */}
                    <motion.div
                        className="fixed inset-x-0 top-[20%] z-[60] mx-auto w-full max-w-lg"
                        initial={{ opacity: 0, scale: 0.96, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 8 }}
                        transition={FRAMER_TRANSITIONS.emphasis}
                    >
                        <div
                            className="overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 shadow-drawer"
                            role="combobox"
                            aria-expanded="true"
                            aria-haspopup="listbox"
                            aria-owns="command-palette-listbox"
                            aria-label="Befehlspalette"
                        >
                            {/* Search Input */}
                            <div className="flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-600 px-4">
                                <Search className="h-4 w-4 text-neutral-400 dark:text-neutral-500 shrink-0" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Suchen oder Aktion ausführen…"
                                    className="h-12 flex-1 bg-transparent text-sm text-neutral-800 dark:text-neutral-200 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 outline-none"
                                    aria-label="Befehlssuche"
                                    aria-controls="command-palette-listbox"
                                    aria-activedescendant={
                                        allFiltered[selectedIndex]?.id || undefined
                                    }
                                />
                                <kbd className="hidden rounded border border-neutral-200 dark:border-neutral-600 px-1.5 py-0.5 text-2xs font-medium text-neutral-400 dark:text-neutral-500 sm:inline">
                                    ESC
                                </kbd>
                            </div>

                            {/* Results */}
                            <div
                                className="max-h-72 overflow-y-auto py-2"
                                id="command-palette-listbox"
                                role="listbox"
                                aria-label="Ergebnisse"
                            >
                                {allFiltered.length === 0 ? (
                                    <p className="px-4 py-6 text-center text-sm text-neutral-400 dark:text-neutral-500">
                                        Keine Ergebnisse für „{query}“
                                    </p>
                                ) : (
                                    groups.map((group) => {
                                        const items = allFiltered.filter((c) => c.group === group);
                                        return (
                                            <div key={group}>
                                                <p className="px-4 py-1.5 text-2xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                                                    {group}
                                                </p>
                                                {items.map((item) => {
                                                    flatIndex++;
                                                    const isSelected = flatIndex === selectedIndex;
                                                    const currentIndex = flatIndex; // Capture for click handler
                                                    return (
                                                        <button
                                                            key={item.id}
                                                            id={item.id}
                                                            type="button"
                                                            role="option"
                                                            aria-selected={isSelected}
                                                            className={cn(
                                                                "flex w-full items-center gap-3 px-4 py-2 text-left text-sm motion-micro feedback-hover transition-colors",
                                                                isSelected
                                                                    ? "bg-brand-50 text-brand-700"
                                                                    : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700",
                                                            )}
                                                            onClick={() => {
                                                                item.action();
                                                                onClose();
                                                            }}
                                                            onMouseEnter={() =>
                                                                setSelectedIndex(currentIndex)
                                                            }
                                                        >
                                                            <item.icon className="h-4 w-4 shrink-0 opacity-60" />
                                                            <span className="flex-1 truncate">
                                                                {item.label}
                                                            </span>
                                                            {item.description && (
                                                                <span className="text-xs text-neutral-400 dark:text-neutral-500">
                                                                    {item.description}
                                                                </span>
                                                            )}
                                                            {isSelected && (
                                                                <CornerDownLeft className="h-3.5 w-3.5 text-neutral-400 dark:text-neutral-500" />
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center gap-4 border-t border-neutral-100 dark:border-neutral-700 px-4 py-2">
                                <span className="flex items-center gap-1 text-2xs text-neutral-400 dark:text-neutral-500">
                                    <kbd className="rounded border border-neutral-200 dark:border-neutral-600 px-1 py-0.5 font-mono">
                                        ↑↓
                                    </kbd>{" "}
                                    Navigation
                                </span>
                                <span className="flex items-center gap-1 text-2xs text-neutral-400 dark:text-neutral-500">
                                    <kbd className="rounded border border-neutral-200 dark:border-neutral-600 px-1 py-0.5 font-mono">
                                        ↵
                                    </kbd>{" "}
                                    Auswählen
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
